/**
 * Sidebar.jsx
 * ui.js의 renderSidebarCurriculum 함수를 React 컴포넌트로 1:1 이관
 * 
 * 원본 함수 매핑:
 * - renderSidebarCurriculum() → Sidebar 컴포넌트
 * - updateUserStatusUI() → UserStatus 서브컴포넌트
 * - showLevelUpEffect() → useLevelUpEffect 훅
 */

import { useCallback, useMemo, useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useLunaStore from '../../store/useLunaStore';
import LevelUpModal from '../modals/LevelUpModal';

// ============================================
// 유틸리티 함수
// ============================================

/**
 * HTML 이스케이프 (XSS 방지)
 */
function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// ============================================
// 사용자 상태 컴포넌트 (XP, 레벨, 연속 학습) - 바닐라 매칭
// ============================================
const UserStatus = memo(function UserStatus() {
  const { userXP, userLevel, userStreak, requiredXP } = useLunaStore();

  const xpPercent = useMemo(() => {
    return Math.min(100, Math.max(0, (userXP / requiredXP) * 100));
  }, [userXP, requiredXP]);

  return (
    <div className="w-full h-auto px-5 pt-2 pb-3 flex flex-col justify-center gap-1.5 shrink-0">
      {/* Pill Gauge Container - Matched to Chat Input Height (52px) */}
      <div className="relative w-full h-[46px] rounded-full bg-slate-800/50 overflow-hidden group">

        {/* Fill Animation */}
        <motion.div
          className="absolute top-0 left-0 h-full bg-slate-200"
          initial={{ width: 0 }}
          animate={{ width: `${xpPercent}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />

        {/* Text Layer - Simple & Clean (No decorations) */}
        <div className="absolute inset-0 flex items-center justify-between px-6 z-10 mix-blend-exclusion text-white">
          <span className="font-bold text-sm">Lv.{userLevel}</span>

          <div className="flex items-center gap-3">
            <span className="text-sm font-bold">{userXP}/{requiredXP} XP</span>
            <span className={`text-sm font-bold flex items-center gap-1 ${userStreak > 0 ? '' : 'opacity-50'}`}>
              🔥 {userStreak}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================================
// AI 아바타 컴포넌트 (포트레이트 형태 - 바닐라 버전 매칭)
// ============================================

// 튜터 이미지 매핑 (표정별)
const TUTOR_IMAGES = {
  default: '/assets/images/tutor/luna_default.png',
  happy: '/assets/images/tutor/luna_happy.png',
  thinking: '/assets/images/tutor/luna_thinking.png',
  encouraging: '/assets/images/tutor/luna_encouraging.png',
  surprised: '/assets/images/tutor/luna_surprised.png',
  proud: '/assets/images/tutor/luna_proud.png',
  teaching: '/assets/images/tutor/luna_teaching.png',
};

// 표정 이모지 매핑
const EMOTION_ICONS = {
  idle: 'fa-face-smile',
  thinking: 'fa-face-meh',
  happy: 'fa-face-laugh-beam',
  teaching: 'fa-face-smile-wink',
  encouraging: 'fa-face-grin-stars',
  proud: 'fa-face-grin-hearts',
  default: 'fa-face-smile',
};

const AIAvatar = memo(function AIAvatar() {
  const { aiStatus, tutorExpression, aiStatusText } = useLunaStore();

  // 표정에 따른 이미지 선택
  const tutorImage = useMemo(() => {
    const expressionToImage = {
      neutral: TUTOR_IMAGES.default,
      default: TUTOR_IMAGES.default,
      happy: TUTOR_IMAGES.happy,
      thinking: TUTOR_IMAGES.thinking,
      encouraging: TUTOR_IMAGES.encouraging,
      surprised: TUTOR_IMAGES.surprised,
      proud: TUTOR_IMAGES.proud,
      teaching: TUTOR_IMAGES.teaching,
    };
    return expressionToImage[tutorExpression] ||
      expressionToImage[aiStatus] ||
      TUTOR_IMAGES.default;
  }, [tutorExpression, aiStatus]);

  // 표정 아이콘
  const emotionIcon = useMemo(() => {
    return EMOTION_ICONS[aiStatus] || EMOTION_ICONS.default;
  }, [aiStatus]);

  // 상태 텍스트
  const statusText = useMemo(() => {
    if (aiStatusText) return aiStatusText;
    const statusTexts = {
      idle: '함께 성장해요!',
      thinking: '생각 중...',
      happy: '잘했어요! 🎉',
      teaching: '설명하고 있어요...',
      encouraging: '힘내요! 💪',
      proud: '대단해요! 🌟',
    };
    return statusTexts[aiStatus] || '준비 완료!';
  }, [aiStatus, aiStatusText]);

  return (
    <div className="flex flex-col shrink-0">
      {/* 포트레이트 이미지 영역 - Simplified */}
      <div className="relative h-56 bg-gradient-to-b from-indigo-950/20 to-slate-900/50 flex items-end justify-center overflow-hidden rounded-2xl mx-2 mt-2 mb-2 border border-white/5 group">
        <img
          src={tutorImage}
          alt="루나"
          className={`relative z-10 w-full h-full object-cover object-top drop-shadow-md opacity-90 transition-transform duration-700 group-hover:scale-105 ${aiStatus === 'thinking' || aiStatus === 'teaching' ? 'animate-pulse' : ''}`}
          onError={(e) => {
            e.target.src = TUTOR_IMAGES.default;
          }}
        />

        {/* Status Overlay - Modern & Minimal */}
        <div className="absolute bottom-3 inset-x-0 flex justify-center z-20">
          <div className="px-3 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center gap-1.5 shadow-lg">
            {/* Live Indicator Dot */}
            <div className={`w-1.5 h-1.5 rounded-full ${aiStatus === 'thinking' ? 'bg-amber-400 animate-ping' : 'bg-emerald-400'}`}></div>
            <span className="text-[10px] font-medium text-white/90 tracking-wide">
              {statusText}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================================
// 커리큘럼 타임라인 아이템 컴포넌트
// ============================================
const TimelineItem = memo(function TimelineItem({
  step,
  index,
  isActive,
  isCompleted,
  onClick,
  isGuest
}) {
  const handleClick = useCallback(() => {
    // [Fix] 게스트 모드에서는 모든 사이드바 아이템 클릭 차단 (현재 단계 포함)
    if (isGuest) {
      // 굳이 알림을 띄우지 않아도 되지만, 명확한 피드백을 위해 유지
      alert('🔒 게스트 모드에서는 샘플 레슨만 체험할 수 있어요.\n로그인 후 전체 커리큘럼을 이용해보세요!');
      return;
    }
    if (step.locked) {
      alert('🔒 이전 단계를 먼저 완료해주세요!');
      return;
    }
    if (onClick) {
      onClick(index, step);
    }
  }, [onClick, index, step, isGuest]);

  const isLocked = step.locked || (isGuest && !isActive);

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      onClick={handleClick}
      className={`
        timeline-item py-2.5 px-3 rounded-md transition-all duration-200 border-l-2
        ${(isGuest || step.locked) ? 'cursor-not-allowed' : 'cursor-pointer'}
        ${isActive
          ? 'bg-slate-800/50 border-cyan-500 text-slate-200'
          : isCompleted
            ? 'bg-transparent border-transparent hover:bg-slate-800/30 text-slate-400'
            : 'bg-transparent border-transparent hover:bg-slate-800/30 opacity-40'
        }
      `}
    >
      <div className="flex items-start gap-3">
        {/* 상태 아이콘 - Simplified (Lock/Dot/Check) */}
        <div className="flex-shrink-0 mt-1">
          {step.locked ? (
            <i className="fas fa-lock text-slate-600 text-[10px]" />
          ) : isActive ? (
            <div className="w-2 h-2 rounded-full bg-cyan-500 shadow-[0_0_8px_rgba(6,182,212,0.8)]"></div>
          ) : isCompleted ? (
            <i className="fas fa-check text-emerald-500 text-xs" />
          ) : (
            <div className="w-1.5 h-1.5 rounded-full bg-slate-700"></div>
          )}
        </div>

        {/* 텍스트 - No Pills */}
        <div className="flex-1 min-w-0">
          <p className={`
            text-sm font-medium leading-snug
            ${isActive ? 'text-slate-200' : isCompleted ? 'text-slate-400' : 'text-slate-500'}
          `}>
            {step.title || `단계 ${index + 1}`}
          </p>
          {isActive && (
            <span className="text-[10px] text-cyan-500/80 font-mono mt-0.5 block">
              STEP {step.step || index + 1}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
});

// ============================================
// 커리큘럼 타임라인 컴포넌트
// ============================================
const CurriculumTimeline = memo(function CurriculumTimeline({ onStepClick }) {
  const {
    currentCurriculum,
    currentStepIndex,
    curriculumProgress,
    username
  } = useLunaStore();

  const isGuest = !username;

  const steps = useMemo(() => {
    return currentCurriculum?.curriculum || [];
  }, [currentCurriculum]);

  // 단계 완료 여부 판정
  const isStepCompleted = useCallback((idx) => {
    // 서버 데이터 우선
    if (curriculumProgress?.steps?.[idx]?.completed) {
      return true;
    }
    // 기존 로직: 현재보다 앞선 단계는 완료 처리
    return idx < currentStepIndex;
  }, [curriculumProgress, currentStepIndex]);

  if (steps.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <p className="text-xs text-gray-500 text-center">
          커리큘럼이 생성되면 여기에 표시됩니다
        </p>
      </div>
    );
  }

  return (
    <div className="curriculum-timeline flex-1 overflow-y-auto p-3 space-y-2">
      <AnimatePresence>
        {steps.map((step, idx) => (
          <TimelineItem
            key={idx}
            step={step}
            index={idx}
            isActive={idx === currentStepIndex}
            isCompleted={isStepCompleted(idx)}
            onClick={onStepClick}
            isGuest={isGuest}
          />
        ))}
      </AnimatePresence>
    </div>
  );
});

// ============================================
// 네비게이션 버튼 컴포넌트
// ============================================
const NavButtons = memo(function NavButtons({ onNewTopic, onRoadmap, onDashboard }) {
  const { isRoadmapMode, username } = useLunaStore();
  const isGuest = !username;

  return (
    <div className="nav-buttons p-3 border-t border-gray-700 space-y-2">
      {/* 새 주제 버튼 */}
      <button
        type="button"
        onClick={isGuest ? () => alert('로그인 후 새로운 주제를 시작할 수 있습니다.') : onNewTopic}
        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 shadow-lg ${isGuest
            ? 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-70'
            : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white hover:shadow-cyan-500/25'
          }`}
        title={isGuest ? "로그인 필요" : "새로운 주제 시작"}
      >
        <i className="fas fa-plus" />
        새 주제
      </button>

      {/* 로드맵 버튼 (로드맵 모드일 때만) */}
      {isRoadmapMode && (
        <button
          onClick={onRoadmap}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-lg text-sm font-semibold transition-all duration-200"
        >
          <i className="fas fa-route" />
          로드맵
        </button>
      )}

      {/* 대시보드 버튼 */}
      <button
        onClick={onDashboard}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
      >
        <i className="fas fa-chart-bar" />
        대시보드
      </button>
    </div>
  );
});

// ============================================
// 메인 Sidebar 컴포넌트
// ============================================
export default function Sidebar({
  onStepClick,
  onNewTopic,
  onRoadmap,
  onDashboard,
  className = '',
  hideHeader = false
}) {
  const { currentCurriculum, isRoadmapMode, roadmapContext, currentRoadmap } = useLunaStore();

  // 커리큘럼 제목
  const curriculumTitle = useMemo(() => {
    return currentCurriculum?.topic || '학습 주제';
  }, [currentCurriculum]);

  // [Fix] 로드맵 데이터가 없으면 불러오기 (조건 완화)
  useEffect(() => {
    if (isRoadmapMode && !currentRoadmap) {
      // 로드맵 데이터 fetch
      import('../../services/api').then(module => {
        module.getRoadmap().then(data => {
          if (data && data.roadmap) {
            useLunaStore.getState().setCurrentRoadmap(data.roadmap);
          }
        }).catch(err => console.warn('로드맵 로드 실패:', err));
      });
    }
  }, [isRoadmapMode, currentRoadmap]);

  // 로드맵 관련 표시 정보 계산
  const { roadmapTitleDisplay, topicTitleDisplay } = useMemo(() => {
    if (!isRoadmapMode) return { roadmapTitleDisplay: '', topicTitleDisplay: '' };

    // 1. 로드맵 제목
    let rTitle = currentRoadmap?.roadmap_title || currentRoadmap?.title;

    // Fallback: roadmapContext에 제목이 있으면 사용
    if (!rTitle && roadmapContext?.roadmapTitle) {
      rTitle = roadmapContext.roadmapTitle;
    }

    // 최후의 수단
    if (!rTitle) rTitle = '로드맵 정보 로딩 중...';

    // 2. Phase 및 Topic 제목
    let tTitle = '';
    let phaseTitle = '';

    // A. currentRoadmap 데이터가 있는 경우 (가장 정확)
    if (currentRoadmap?.phases) {
      let pIdx = roadmapContext?.pIdx;
      let tIdx = roadmapContext?.tIdx;

      // [Fix] 인덱스 정보가 없으면 토픽 제목으로 역추적
      if (pIdx === undefined && currentCurriculum?.topic) {
        // 현재 토픽 제목으로 Phase/Topic 위치 찾기
        currentRoadmap.phases.some((p, pi) => {
          if (p.topics) {
            const ti = p.topics.findIndex(t => t.title === currentCurriculum.topic);
            if (ti !== -1) {
              pIdx = pi;
              tIdx = ti;
              return true;
            }
          }
          return false;
        });
      }

      if (pIdx !== undefined && currentRoadmap.phases[pIdx]) {
        const phase = currentRoadmap.phases[pIdx];
        phaseTitle = phase.title; // Phase 제목 획득

        if (tIdx !== undefined && phase.topics?.[tIdx]) {
          tTitle = phase.topics[tIdx].title;
        } else {
          // 토픽 인덱스가 없는데 토픽 정보가 필요하다면?
          // 일단 페이즈 제목을 쓰거나, currentCurriculum.topic 사용
          tTitle = currentCurriculum?.topic || phase.title;
        }
      }
    }

    // B. 데이터 로드 전, Context만 있는 경우
    if (!phaseTitle && roadmapContext) {
      tTitle = roadmapContext.topic || roadmapContext.phaseTitle;
      if (roadmapContext.phaseTitle) phaseTitle = roadmapContext.phaseTitle;
    }

    // C. [Fix] 아직도 제목이 없으면 currentCurriculum 사용
    if (!tTitle && currentCurriculum?.topic) {
      tTitle = currentCurriculum.topic;
    }

    if (!tTitle) tTitle = '토픽 정보 없음';

    // [User Request] 표시 구조 변경: 로드맵명 > Phase명 > Topic명
    let combinedSubTitle = tTitle;

    if (phaseTitle) {
      // Phase 제목이 Topic 제목과 다를 때만 결합
      if (phaseTitle !== tTitle) {
        // Phase 제목이 너무 길면 "Phase N"만 추출하는 등 처리가 필요할 수 있으나
        // 일단 전체 표시 요청에 따름
        combinedSubTitle = `${phaseTitle} > ${tTitle}`;
      }
    }

    return { roadmapTitleDisplay: rTitle, topicTitleDisplay: combinedSubTitle };
  }, [isRoadmapMode, currentRoadmap, roadmapContext, currentCurriculum]);

  return (
    <aside className={`flex flex-col h-full bg-black/40 backdrop-blur-none border-r-0 relative z-30 transition-all duration-300 shadow-none ${className}`}>

      {/* 1. 프로필 섹션 (User Profile) - 컴팩트 디자인 적용 */}
      {!hideHeader && (
        <div className="glass-panel rounded-2xl p-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <i className="fas fa-moon text-white text-lg" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">LUNA</h1>
              <p className="text-xs text-gray-400">Coding Tutor</p>
            </div>
          </div>
        </div>
      )}

      {/* A. AI 튜터 루나 - 포트레이트 형태 */}
      <AIAvatar />

      {/* B. 커리큘럼 타임라인 */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* 현재 커리큘럼 제목 */}
        {(currentCurriculum || isRoadmapMode) && (
          <div className="shrink-0 bg-transparent flex flex-col">


            {/* [신규] 로드맵 모드일 때: 보라색(Roadmap) or 초록색(Course) 헤더 */}
            {isRoadmapMode && (
              <div className={`
                p-4 border-b relative overflow-hidden group transition-colors
                ${currentRoadmap?.type === 'course'
                  ? 'bg-gradient-to-r from-emerald-900/20 to-teal-900/20 border-emerald-500/10 hover:from-emerald-900/30 hover:to-teal-900/30'
                  : 'bg-gradient-to-r from-purple-900/20 to-indigo-900/20 border-purple-500/10 hover:from-purple-900/30 hover:to-indigo-900/30'}
              `}>
                {/* 배경 장식 */}
                <div className={`
                  absolute -right-4 -top-4 w-16 h-16 rounded-full blur-2xl transition-all
                  ${currentRoadmap?.type === 'course'
                    ? 'bg-emerald-500/10 group-hover:bg-emerald-500/20'
                    : 'bg-purple-500/10 group-hover:bg-purple-500/20'}
                `}></div>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2">
                    {/* Header Label */}
                    <div className={`
                      flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider
                      ${currentRoadmap?.type === 'course' ? 'text-emerald-400' : 'text-purple-400'}
                    `}>
                      <i className={`
                        fas 
                        ${currentRoadmap?.type === 'course' ? 'fa-graduation-cap text-emerald-500' : 'fa-map-marked-alt text-purple-500'}
                      `}></i>
                      <span>
                        {currentRoadmap?.type === 'course' ? 'Running Master Course' : 'Running Roadmap'}
                      </span>
                    </div>
                    {/* View All */}
                    <button
                      onClick={onRoadmap}
                      className={`
                        text-[10px] transition-colors flex items-center gap-1 group/btn
                        ${currentRoadmap?.type === 'course' ? 'text-emerald-400/60 hover:text-emerald-300' : 'text-purple-400/60 hover:text-purple-300'}
                      `}
                    >
                      <span>전체보기</span>
                      <i className="fas fa-arrow-right text-[9px] group-hover/btn:translate-x-0.5 transition-transform"></i>
                    </button>
                  </div>

                  {/* 1. Roadmap Title */}
                  <div className={`
                    text-[10px] font-medium mb-0.5 truncate
                    ${currentRoadmap?.type === 'course' ? 'text-emerald-300/60' : 'text-purple-300/60'}
                  `}>
                    {roadmapTitleDisplay}
                  </div>

                  {/* 2. Phase Title (Topic) */}
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`
                      w-1 h-3 rounded-full shrink-0 
                      ${currentRoadmap?.type === 'course'
                        ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]'
                        : 'bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.5)]'}
                    `}></div>
                    <div className="text-sm font-bold text-slate-200 leading-tight truncate">
                      {topicTitleDisplay}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 커리큘럼 헤더 */}
            <div className={`px-4 py-3 border-b border-white/5 ${!isRoadmapMode ? 'bg-white/5' : 'bg-black/10'}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="text-xs font-bold text-slate-200 leading-tight whitespace-normal break-words flex-1 cursor-help" title={curriculumTitle}>
                  {curriculumTitle}
                </h3>
                {/* Steps Badge: Minimal Text */}
                <span className="shrink-0 text-[10px] text-slate-500 font-medium mt-0.5 flex items-center gap-1">
                  <span className="text-slate-400 font-bold">{currentCurriculum?.curriculum?.length || 0}</span> Steps
                </span>
              </div>

              {/* 진행률 바 (미니) */}
              {currentCurriculum?.curriculum?.length > 0 && (
                <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden mt-2">
                  <div
                    className="h-full bg-cyan-600 rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, (useLunaStore.getState().currentStepIndex / currentCurriculum.curriculum.length) * 100)}%`
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {/* 커리큘럼 타임라인 */}
        <CurriculumTimeline onStepClick={onStepClick} />
      </div>


      {/* C. XP 대시보드 - Footer Style (Attached) */}
      <div className="mt-auto flex flex-col">
        <UserStatus />
      </div>
    </aside>
  );
}


// ============================================
// 레벨업 효과 훅 (export for use in other components)
// ============================================

export function useLevelUpEffect() {
  const [showEffect, setShowEffect] = useState(false);
  const [newLevel, setNewLevel] = useState(0);

  const triggerLevelUp = useCallback((level) => {
    setNewLevel(level);
    setShowEffect(true);
  }, []);

  const LevelUpOverlay = useMemo(() => {
    return (
      <LevelUpModal
        show={showEffect}
        level={newLevel}
        onClose={() => setShowEffect(false)}
      />
    );
  }, [showEffect, newLevel]);

  return { triggerLevelUp, LevelUpOverlay };
}

// ============================================
// XP 보상 처리 훅
// ============================================
export function useRewardHandler() {
  const {
    setUserXP,
    setUserLevel,
    setUserStreak,
    setRequiredXP,
    addChatMessage
  } = useLunaStore();

  /* 
   * [Fix] useLunaStore() returns state object, but inside a callback, 
   * accessing state via getState() is safer to avoid stale closures or selector issues.
   * Also, accessing methods directly from store avoids "is not a function" if selector logic fails.
   */
  const handleReward = useCallback((reward) => {
    console.log('[handleReward] Called with:', reward);
    if (!reward) {
      console.log('[handleReward] No reward data, skipping...');
      return;
    }

    // Direct store access
    const { setUserXP, setUserLevel, setUserStreak, setRequiredXP, addChatMessage } = useLunaStore.getState();

    console.log('[handleReward] Updating store with:', {
      current_xp: reward.current_xp,
      current_level: reward.current_level,
      streak: reward.streak,
      required_xp: reward.required_xp,
      leveled_up: reward.leveled_up
    });

    // 상태 업데이트
    if (setUserXP) setUserXP(reward.current_xp);
    if (setUserLevel) setUserLevel(reward.current_level);
    if (setUserStreak) setUserStreak(reward.streak);
    if (setRequiredXP) setRequiredXP(reward.required_xp);

    console.log('[handleReward] Store updated. Checking state:', useLunaStore.getState().userXP);

    // 레벨업 효과
    if (reward.leveled_up) {
      // [Refactor] 전역 스토어 사용
      useLunaStore.getState().setLevelUpData({ level: reward.current_level });

      if (addChatMessage) {
        addChatMessage({
          role: 'ai',
          content: `🎉 레벨 ${reward.current_level}에 도달했습니다! 축하해요!`,
        });
      }
    }
  }, []); // Removed store actions from dependency array

  return { handleReward };
}
